import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, ShieldCheck, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quality() {
  const [samples, setSamples] = useState([]);
  const [orders, setOrders] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    order_id: "",
    sample_size: "",
    defects_found: "",
    defect_types: [],
    notes: "",
    passed: true,
  });

  const fetchData = async () => {
    try {
      const [samplesRes, ordersRes, defectsRes] = await Promise.all([
        axios.get(`${API}/quality-controls`),
        axios.get(`${API}/orders`),
        axios.get(`${API}/defects`),
      ]);
      setSamples(samplesRes.data);
      setOrders(ordersRes.data.filter((o) => ["running", "paused"].includes(o.status)));
      setDefects(defectsRes.data.defects || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      order_id: "",
      sample_size: "",
      defects_found: "",
      defect_types: [],
      notes: "",
      passed: true,
    });
  };

  const handleDefectToggle = (defect) => {
    setFormData((prev) => ({
      ...prev,
      defect_types: prev.defect_types.includes(defect)
        ? prev.defect_types.filter((d) => d !== defect)
        : [...prev.defect_types, defect],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      order_id: formData.order_id,
      control_type: "visual",
      sample_size: parseInt(formData.sample_size),
      results: {
        defects_found: parseInt(formData.defects_found) || 0,
        defect_types: formData.defect_types,
      },
      observations: formData.notes || null,
      passed: formData.passed,
    };

    try {
      await axios.post(`${API}/quality-controls`, payload);
      toast.success("Controlo registado com sucesso");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      showError(error, "Erro ao registar controlo");
    }
  };

  const getOrderInfo = (orderId) => {
    const order = orders.find((o) => o.id === orderId);
    return order ? order.code : "N/A";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quality-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Controlo de Qualidade
          </h1>
          <p className="text-muted-foreground mt-1">
            Registo de amostras e inspeções
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-sample-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Amostra
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">Registar Amostra de Qualidade</DialogTitle>
              <DialogDescription>
                Registe a inspeção de amostragem
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="order_id">Ordem de Produção *</Label>
                  <select
                    id="order_id"
                    value={formData.order_id}
                    onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="sample-order"
                  >
                    <option value="">Selecione uma ordem</option>
                    {orders.map((order) => (
                      <option key={order.id} value={order.id}>
                        {order.code}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sample_size">Tamanho da Amostra *</Label>
                  <Input
                    id="sample_size"
                    type="number"
                    min="1"
                    value={formData.sample_size}
                    onChange={(e) => setFormData({ ...formData, sample_size: e.target.value })}
                    placeholder="Ex: 50"
                    required
                    data-testid="sample-size"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="defects_found">Defeitos Encontrados</Label>
                  <Input
                    id="defects_found"
                    type="number"
                    min="0"
                    value={formData.defects_found}
                    onChange={(e) => setFormData({ ...formData, defects_found: e.target.value })}
                    placeholder="0"
                    data-testid="defects-found"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Resultado</Label>
                  <div className="flex gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="passed"
                        checked={formData.passed}
                        onChange={() => setFormData({ ...formData, passed: true })}
                        className="w-4 h-4"
                      />
                      <span className="text-emerald-500 font-medium">Aprovado</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="passed"
                        checked={!formData.passed}
                        onChange={() => setFormData({ ...formData, passed: false })}
                        className="w-4 h-4"
                      />
                      <span className="text-red-500 font-medium">Reprovado</span>
                    </label>
                  </div>
                </div>
              </div>

              {parseInt(formData.defects_found) > 0 && (
                <div className="space-y-3">
                  <Label>Tipos de Defeito Encontrados</Label>
                  <div className="grid grid-cols-2 gap-2 p-4 bg-secondary/30 rounded-sm max-h-48 overflow-y-auto">
                    {defects.map((defect) => (
                      <label
                        key={defect}
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={formData.defect_types.includes(defect)}
                          onCheckedChange={() => handleDefectToggle(defect)}
                        />
                        <span>{defect}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="notes">Observações</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionais..."
                  data-testid="sample-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-sample-btn">
                  Registar Amostra
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Samples Table */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Amostras de Qualidade ({samples.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {samples.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Ordem</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Tamanho</TableHead>
                    <TableHead>Defeitos</TableHead>
                    <TableHead>Resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {samples.map((sample) => (
                    <TableRow key={sample.id} data-testid={`sample-row-${sample.id}`}>
                      <TableCell className="font-mono text-sm text-foreground">
                        {new Date(sample.created_at).toLocaleString("pt-PT")}
                      </TableCell>
                      <TableCell className="font-mono font-bold text-foreground">
                        {getOrderInfo(sample.order_id)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {sample.control_type || "visual"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-foreground">{sample.sample_size}</TableCell>
                      <TableCell className="font-mono text-foreground">
                        {sample.results?.defects_found || 0}
                      </TableCell>
                      <TableCell>
                        {sample.passed ? (
                          <Badge className="bg-emerald-500 text-white">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Aprovado
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500 text-white">
                            <XCircle className="h-3 w-3 mr-1" />
                            Reprovado
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <ShieldCheck className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhuma amostra registada</p>
              <p className="text-sm">Clique em "Nova Amostra" para começar</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Defects Reference */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo text-lg">
            Referência de Defeitos de Injeção
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {defects.map((defect) => (
              <div
                key={defect}
                className="p-2 bg-secondary/30 rounded-sm text-sm"
              >
                {defect}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
